import { useMemo, type ReactNode } from 'react';
import { Edit, Eraser, Plus, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, ManagedDataTable } from '@/design-system';
import type { StaffUser } from '../api/users.service';
import { USER_ROLE_LABELS } from '../utils/user-role-labels';

export interface UserTableHandlers {
  onEdit: (user: StaffUser) => void;
  onDelete: (user: StaffUser) => void;
  onAnonymize?: (user: StaffUser) => void;
  onToggleActive: (user: StaffUser) => void;
  onCreate: () => void;
}

interface UserTableProps {
  filters?: ReactNode;
  users: StaffUser[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
  search: string;
  onSearchChange: (v: string) => void;
  canCreate: boolean;
  canDelete: boolean;
  currentUserId: string | undefined;
  handlers: UserTableHandlers;
}

export function UserTable({
  filters,
  users,
  loading,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  search,
  onSearchChange,
  canCreate,
  canDelete,
  currentUserId,
  handlers,
}: UserTableProps) {
  const columns = useMemo<ColumnDef<StaffUser>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => <div className="text-sm font-medium text-gray-900">{row.original.name}</div>,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <div className="text-sm text-gray-500">{row.original.email}</div>,
      },
      {
        accessorKey: 'role',
        header: 'Perfil',
        cell: ({ row }) => (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            {USER_ROLE_LABELS[row.original.role] || row.original.role}
          </span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Button variant="ghost" size="sm"
            type="button"
            onClick={() => handlers.onToggleActive(row.original)}
            className="flex items-center gap-2"
            disabled={currentUserId === row.original.id}
          >
            {row.original.isActive ? (
              <ToggleRight size={20} className="text-green-600" />
            ) : (
              <ToggleLeft size={20} className="text-gray-400" />
            )}
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                row.original.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {row.original.isActive ? 'Ativo' : 'Inativo'}
            </span>
          </Button>
        ),
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => handlers.onEdit(row.original)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Editar"
            >
              <Edit size={16} />
            </Button>
            {canDelete && currentUserId !== row.original.id && (
              <Button variant="ghost" size="sm"
                type="button"
                onClick={() => handlers.onDelete(row.original)}
                className="text-gray-400 hover:text-red-600 transition-colors"
                title="Deletar (soft)"
                aria-label="Deletar"
              >
                <Trash2 size={16} />
              </Button>
            )}
            {canDelete && handlers.onAnonymize && currentUserId !== row.original.id && (
              <Button variant="ghost" size="sm"
                type="button"
                onClick={() => handlers.onAnonymize!(row.original)}
                className="text-gray-400 hover:text-rose-700 transition-colors"
                title="Excluir permanentemente (LGPD)"
                aria-label="Excluir permanentemente (LGPD)"
              >
                <Eraser size={16} />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [handlers, canDelete, currentUserId]
  );

  return (
    <ManagedDataTable
      title="Lista de usuários"
      subtitle="Gerencie os usuários do Manager, seus perfis e permissões de acesso."
      toolbarFilters={filters}
      columns={columns}
      data={users}
      loading={loading}
      searchPlaceholder="Buscar por nome ou email..."
      searchValue={search}
      onSearchChange={onSearchChange}
      toolbarButtons={
        canCreate
          ? [
              {
                label: 'Novo Usuário',
                icon: <Plus size={16} />,
                onClick: handlers.onCreate,
              },
            ]
          : []
      }
      pagination={{ page, totalPages, total, limit, onPageChange }}
      emptyMessage="Nenhum usuário encontrado."
    />
  );
}
